<template>
  <div class="music-playback-container">
    <div class="gradient-background"></div>
    <!-- 左侧面板 -->
    <div class="floating-panel left-panel">
      <div class="panel-header">
        <div class="left-title"> {{ $translate('robotList') }}</div>
      </div>
      <div class="robot-list">
        <div class="robot-item"
             v-for="(item, index) in robotList"
             :key="index"
             @click="selectRobot(index)"
             :class="{ 'active': currentRobot && currentRobot.id === item.id }">
          <div class="robot-avatar">
            <img :src="item.avatar" alt="robot avatar">
          </div>
          <div class="robot-info">
            <div class="robot-name">{{item.name}}</div>
            <div class="robot-serial">{{item.serialNumber}}</div>
            <div class="robot-song" v-if="item.currentSongName">
              {{$translate('currentPlay') + ':' + item.currentSongName}}
            </div>
          </div>
          <div class="robot-controls">
            <!-- 添加同步按钮 -->
            <div class="sync-btn" @click.stop="syncRobot(index)" :title="$translate('sync.files')">
              <svg-icon name="sync" class="sync-icon" />
            </div>
            <!-- 播放状态按钮 -->
            <div class="play-status"
                 :class="{
                   'playing': item.isPlaying,
                   'preparing': item.playState === 3,
                   'checking': checkingIntercomRobots.has(item.serialNumber)
                 }"
                 @click.stop="toggleRobotPlay(index)">
              <!-- 检查对讲状态时显示加载动画 -->
              <div class="loading-animation" v-if="checkingIntercomRobots.has(item.serialNumber)" :title="$translate('checkingIntercom')">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <!-- 准备中状态显示加载动画 -->
              <div class="loading-animation" v-else-if="item.playState === 3" :title="$translate('preparing')">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <!-- 不在播放时显示播放键 -->
              <div class="play-triangle" v-else-if="!item.isPlaying" :title="$translate('play')"></div>
              <!-- 播放时显示波浪动画 -->
              <div class="wave-animation" v-else :title="$translate('pause')">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>

            <!-- 添加停止播放按钮 -->
            <div class="stop-btn" @click.stop="stopRobotPlay(index)" :title="$translate('stopPlaying')">
              <svg-icon name="stopPlaying" class="stop-icon" />
            </div>
          </div>
        </div>
      </div>

      <!-- 添加分页组件 -->
      <div class="pagination">
        <div class="page-btn prev" @click="changePage(page - 1)" :class="{ disabled: page <= 1 }" :title="$translate('previousPage')">
          <svg-icon name="arrow" class="page-icon arrow-left" />
        </div>

        <div class="page-info">
          <div class="current-page">{{ page }}</div>
          <div class="page-divider"></div>
          <div class="total-page">{{ current }}</div>
        </div>

        <div class="page-btn next" @click="changePage(page + 1)" :class="{ disabled: page >= current }" :title="$translate('nextPage')">
          <svg-icon name="arrow" class="page-icon arrow-right" />
        </div>
      </div>
    </div>

    <!-- 右侧面板 -->
    <div class="floating-panel right-panel">
      <div class="player-container">
        <!-- 播放图片区域 -->
        <div class="player-image">
          <canvas ref="particleCanvas" class="particle-canvas"></canvas>
          <div class="player-info" v-if="currentRobot">
            <div class="current-name">{{currentRobot.name}}</div>
            <div class="current-serial">{{currentRobot.serialNumber}}</div>
            <div class="current-song" v-if="currentSongName">
              {{currentSongName}}
            </div>
          </div>
        </div>

        <!-- 音频波浪动画 -->
        <div class="wave-container" :class="{ 'playing': isPlaying }" ref="waveContainer">
          <div class="wave-bar" v-for="n in waveCount" :key="n" :class="{ 'short': n % 2 === 0 }"></div>
        </div>

        <!-- 播放控制区域 -->
        <div class="player-controls">
          <!-- 播放模式控制 -->
          <div class="mode-controls">
            <div class="mode-btn" :class="{ active: playMode === 'random' }" @click="togglePlayMode('random')" :title="$translate('randomPlay')">
              <svg-icon name="random" class="mode-icon" />
            </div>
            <div class="mode-btn" :class="{ active: playMode === 'sequence' }" @click="togglePlayMode('sequence')" :title="$translate('sequencePlay')">
              <svg-icon name="sequential" class="mode-icon" />
            </div>
            <div class="mode-btn" :class="{ active: playMode === 'single' }" @click="togglePlayMode('single')" :title="$translate('singleLoop')">
              <svg-icon name="loopPlayback" class="mode-icon" />
            </div>
          </div>

          <!-- 主播放按钮 -->
          <div class="control-buttons">
            <!-- 添加上一首按钮 -->
            <div class="next-prev-btn prev-btn" @click="playPreviousSong" :title="$translate('previousSong')">
              <svg-icon name="Previous" class="control-icon" />
            </div>

            <div class="main-play-btn"
                 :class="{ 'playing': isPlaying, 'preparing': currentRobot && currentRobot.playState === 3 }"
                 @click="togglePlay"
                 :title="currentRobot && currentRobot.playState === 3 ? $translate('preparing') : (isPlaying ? $translate('pause') : $translate('play'))">
              <!-- 准备中状态显示加载动画 -->
              <div class="loading-animation" v-if="currentRobot && currentRobot.playState === 3">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <!-- 正常播放状态 -->
              <svg-icon
                v-else
                :name="isPlaying ? 'pause' : 'play'"
                class="play-icon"
                :class="isPlaying ? 'pause-btn-status' : 'play-btn-status'"
              ></svg-icon>
            </div>

            <!-- 添加下一首按钮 -->
            <div class="next-prev-btn next-btn" @click="playNextSong" :title="$translate('nextSong')">
              <svg-icon name="NextSong" class="control-icon" />
            </div>
          </div>

          <!-- 音量控制 -->
          <div class="volume-control">
            <div class="volume-icon" :class="{ 'muted': isMuted }" @click="toggleMute" :title="isMuted ? $translate('unmute') : $translate('mute')">
              <svg-icon :name="isMuted ? 'mute' : 'noMute'" class="mode-icon" />
            </div>
            <div class="volume-slider" :title="$translate('adjustVolume')">
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                v-model="volume"
                @wheel.prevent="handleVolumeWheel"
                @change="updateVolumeStatus"
              >
            </div>
            <!-- 音量显示 -->
            <div class="volume-display">
              <span class="volume-value">{{ Math.round(volume * 100) }}%</span>
            </div>
            <!-- 添加歌曲列表按钮 -->
            <div class="playlist-btn" @click="togglePlaylist" :title="$translate('songList')">
              <svg-icon name="playlist" class="mode-icon" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 修改歌曲列表抽屉 -->
    <div class="playlist-drawer" :class="{ 'show': showPlaylist }">
      <div class="drawer-header">
        <div class="drawer-title">{{$translate('songList')}}</div>
        <div class="close-btn" @click="togglePlaylist" :title="$translate('closeSongList')">
          <svg-icon name="close" class="close-icon" />
        </div>
      </div>
      <div class="song-list">
        <div
          v-for="(song, index) in songList"
          :key="index"
          class="song-item"
          :class="{
            'playing': currentRobot && currentRobot.currentSongName === song.fileName && currentRobot.playState === 4,
            'preparing': currentRobot && currentRobot.currentSongName === song.fileName && currentRobot.playState === 3,
            'checking': currentRobot && checkingIntercomRobots.has(currentRobot.serialNumber)
          }"
        >
          <div class="song-info">
            <div class="song-name">{{ song.fileName }}</div>
          </div>
          <div class="song-controls">
            <div class="play-btn"
                 @click="playSong(song)"
                 :class="{
                   'playing': currentRobot && currentRobot.currentSongName === song.fileName && currentRobot.playState === 4,
                   'preparing': currentRobot && currentRobot.currentSongName === song.fileName && currentRobot.playState === 3,
                   'checking': currentRobot && checkingIntercomRobots.has(currentRobot.serialNumber)
                 }"
                 :title="currentRobot && currentRobot.currentSongName === song.fileName ?
                        (currentRobot.playState === 3 ?
                          $translate('preparing') :
                          (currentRobot.isPlaying ? $translate('pause') : $translate('play'))
                        ) : $translate('play')">
              <!-- 检查对讲状态时显示加载动画 -->
              <div class="loading-animation" v-if="currentRobot && checkingIntercomRobots.has(currentRobot.serialNumber)">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <!-- 准备中状态显示加载动画 -->
              <div class="loading-animation" v-else-if="currentRobot && currentRobot.currentSongName === song.fileName && currentRobot.playState === 3">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <!-- 正常播放状态 -->
              <svg-icon
                v-else
                :name="currentRobot && currentRobot.currentSongName === song.fileName && currentRobot.isPlaying ? 'pause' : 'play'"
                class="play-icon"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/index'
import { getTenantId } from "@/libs/util";
import { sendMsg, getMvpUrlMap, connectWithUrl } from "@/utils/websocket.js";

export default {
  data() {
    return {
      robotList: [],
      currentRobot: null,
      isPlaying: false,
      playMode: 'sequence', // 'sequence' or 'random' or 'single'
      volume: 0.5,
      isMuted: false,
      lastVolume: 0.5,
      waveCount: 0,
      particles: [],
      stars: [],
      notes: [],
      particleCount: 80,
      starCount: 100,
      noteCount: 4,
      time: 0,
      hueRotate: 0,
      canvas: null,
      ctx: null,
      animationFrame: null,
      lastTime: 0,
      total: 0,
      current: 1,
      page:1,
      pageSize:15,
      tenantId:getTenantId(),
      userId:this.$store.state.user.userInfo.userId,
      wsConnected: false, // WebSocket 连接状态
      'audio-playUrl':'notice-server/cmd/audio/play',
      'audio-volumeUrl':'notice-server/cmd/audio/volume',
      'audio-muteUrl':'notice-server/cmd/audio/mute',
      volumeDebounceTimer: null,  // 添加防抖定时器
      showPlaylist: false,
      songList: [],
      successDict: {
        'cmd_resp_play_command_success': this.$translate('playCommandSuccess'),
        'cmd_resp_play_mode_success': this.$translate('playModeSuccess'),
        'cmd_resp_pause_or_resume_success': this.$translate('pauseOrResumeSuccess'),
        'cmd_resp_set_volume_success': this.$translate('setVolumeSuccess'),
        'cmd_resp_toggle_mute_success': this.$translate('toggleMuteSuccess'),
        'cmd_resp_play_list_success': this.$translate('playListSuccess')
      },
      errorDict: {
        'cmd_resp_play_command_error': this.$translate('playCommandError'),
        'cmd_resp_play_mode_error': this.$translate('playModeError'),
        'cmd_resp_pause_or_resume_error': this.$translate('pauseOrResumeError'),
        'cmd_resp_set_volume_error': this.$translate('setVolumeError'),
        'cmd_resp_toggle_mute_error': this.$translate('toggleMuteError'),
        'cmd_resp_play_list_error': this.$translate('playListError'),
        'error': this.$translate('unknownError')
      },
      previousStates: {}, // 用于存储各机器人的先前状态
      musicSocket:null,
      VoiceIntercomURL:getMvpUrlMap,
      checkingIntercomRobots: new Set() // 存储正在检查对讲状态的机器人ID
    }
  },
  async mounted() {
    // 手动关闭所有可能存在的Modal弹窗
    this.$Modal.remove();
    await this.getList();
    this.calculateWaveCount();
    this.initParticleAnimation();
    window.addEventListener('resize', this.calculateWaveCount);
    // 初始化 WebSocket 连接
    this.initWebSocket();
    this.initRobot();
    setInterval(this.initRobot, 120000);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateWaveCount);
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    // 关闭WebSocket连接
    if (this.musicSocket) {
      this.musicSocket.close && this.musicSocket.close();
    }
    window.musicSocket = null
  },
  methods: {
    initRobot(id){
      if(id){
        sendMsg("musicSocketUrl",JSON.stringify({
            tenantId:this.tenantId,
            robotId:id,
            language:this.$store.state.app.language,
            type:'refresh_volume_status'
          }))
      }else{
        for(let item of this.robotList){
          sendMsg("musicSocketUrl",JSON.stringify({
            tenantId:this.tenantId,
            robotId:item.id,
            language:this.$store.state.app.language,
            type:'refresh_volume_status'
          }))
        }
      }

    },
    // 初始化 WebSocket 连接
    initWebSocket() {
      connectWithUrl('/sound', "musicSocketUrl", JSON.stringify({
          tenantId: this.tenantId,
          type: `init:${this.userId}`,
          language: this.$store.state.app.language,
        }), (data) => {
        this.handleWebSocketMessage(data);
        // 调用全局处理器以便其他组件可以处理消息
        /* if (window.musicSocketMessageHandler) {
          window.musicSocketMessageHandler(data);
        } */
      }, (socket) => {
        this.musicSocket = socket;
        // 设置全局变量以便其他组件可以访问
        window.musicSocket = socket;
        this.initRobot()
      });
    },

    // 处理 WebSocket 消息
    handleWebSocketMessage(message) {
      try {
        console.log(message);
        const data = typeof message === 'string' ? JSON.parse(message) : message;

        switch(data.type) {
          case 'speaker_volume':
            this.updateSpeakerVolume(data);
            break;
          case 'play_status':
            this.updatePlayStatus(data);
            break;
          default:
            // 统一处理成功和错误消息
            if (this.successDict[data.type]) {
              this.$Notice.success({
                title: this.$translate('success'),
                desc: `${this.successDict[data.type]}: ${data.data || ''}`
              });
              this.initRobot(data.robotId);
            } else if (this.errorDict[data.type]) {
              this.$Notice.error({
                title: this.$translate('error'),
                desc: `${this.errorDict[data.type]}: ${data.data || ''}`
              });

              // 根据错误类型回滚状态
              if (data.robotId) {
                this.rollbackState(data.type, data.robotId);
              }
            } else {
              console.log('未处理的WebSocket消息类型:', data.type);
            }
            break;
        }
      } catch (error) {
        console.error('处理 WebSocket 消息出错:', error);
      }
    },

    // 根据错误类型回滚状态
    rollbackState(errorType, robotId) {
      const { robot, isCurrentRobot } = this.getRobot(robotId);
      if (!robot) return;

      // 获取该机器人的先前状态
      const previousState = this.previousStates[robotId];
      if (!previousState) return;

      // 根据错误类型回滚状态
      if (errorType.includes('play_command_error') || errorType.includes('pause_or_resume_error')) {
        if (previousState.playState !== undefined) {
          robot.playState = previousState.playState;
          robot.isPlaying = previousState.playState === 3;

          if (isCurrentRobot) {
            this.isPlaying = robot.isPlaying;
            this.playState = robot.playState;
          }
        }
      } else if (errorType.includes('play_mode_error')) {
        if (previousState.playMode !== undefined) {
          robot.playMode = previousState.playMode;

          if (isCurrentRobot) {
            this.playMode = robot.playMode;
          }
        }
      } else if (errorType.includes('set_volume_error')) {
        if (previousState.volume !== undefined) {
          robot.volume = previousState.volume;

          if (isCurrentRobot) {
            this.volume = robot.volume / 100;
          }
        }
      } else if (errorType.includes('toggle_mute_error')) {
        if (previousState.muted !== undefined) {
          robot.muted = previousState.muted;

          if (isCurrentRobot) {
            this.isMuted = robot.muted;
          }
        }
      }
    },

    // 更新扬声器音量数据
    updateSpeakerVolume(data) {
      const { robot, isCurrentRobot } = this.getRobot(data.robotId);
      if (!robot) return;

      robot.volume = data.data.volume;
      robot.muted = data.data.muted;

      // 如果这个机器人正在显示在右侧面板，更新音量信息
      if (isCurrentRobot) {
        this.volume = data.data.volume / 100;
        this.isMuted = data.data.muted;
      }
    },

    // 更新播放状态
    updatePlayStatus(data) {
      const { robot, isCurrentRobot } = this.getRobot(data.robotId);
      if (!robot) return;

      robot.playState = data.data.playState;
      // 更新播放状态: 4表示播放中
      robot.isPlaying = data.data.playState === 4;
      robot.currentSongName = data.data.filename || '';

      // 更新模式映射
      const modeMap = {
        1: 'sequence', // 列表循环
        2: 'random',   // 随机播放
        3: 'single'    // 单曲循环
      };
      robot.playMode = modeMap[data.data.playMode] || 'sequence';

      if (isCurrentRobot) {
        this.isPlaying = robot.isPlaying;
        this.currentSongName = robot.currentSongName;
        this.playMode = robot.playMode;
        this.playState = robot.playState;
      }
    },

    // 获取机器人列表
    getList() {
      this.total = 0;
      return new Promise((resolve, reject) => {
        api
          .getData(`robot-server/robot/list?index=${this.page}`)
          .then(res => {
            if (res.state === 0) {
              this.$Notice.error({
                title: this.$translate('tip'),
                desc: res.msg
              });
              reject(new Error(res.msg));
            } else {
              this.robotList = res.data.rows.map((j, idx) => ({
                serialNumber: j.code,
                name: j.name,
                avatar: j.avatar || require('@/assets/images/voice/Robot-icon.png'),
                isPlaying: false,
                id: j.id,
                volume: 0,  // 添加音量属性
                muted: false,  // 添加静音属性
                currentSongName: '', // 添加当前播放歌曲名称
                playMode: 'sequence', // 添加播放模式属性
                currentSoundId: null, // 添加 currentSoundId 属性
                playState: 1, // 添加播放状态字段，1:已停止 2:暂停中 3:做准备 4:播放中
              }));
              this.total = res.data.total || 0;
              this.current = Math.ceil(this.total / this.pageSize);
              this.musicSocket && this.initRobot()
              resolve(this.robotList);
            }
          })
          .catch(error => {
            this.$Notice.error({
              title: this.$translate('error'),
              desc: this.$translate('getRobotListError')
            });
            reject(error);
          });
      });
    },
    // 选择机器人
    selectRobot(index) {
      const robot = this.getRobotByIndex(index);
      if (!robot) return;

      this.currentRobot = robot;

      // 更新右侧面板显示的信息
      this.volume = robot.volume / 100;
      this.isMuted = robot.muted;
      this.isPlaying = robot.isPlaying;
      this.currentSongName = robot.currentSongName;
      this.playMode = robot.playMode || 'sequence';
      this.playState = robot.playState;
    },
    // 切换播放/暂停状态
    togglePlay() {
      if (!this.currentRobot) return;
       // 检查对讲状态
       this.checkIntercomStatus(this.currentRobot.serialNumber).then(canProceed => {
        if (!canProceed) {
          return; // 如果不能继续，直接返回
        }
        // 原有的切换播放/暂停逻辑
        this.togglePlayPause(this.currentRobot, true);
      });
    },
    // 切换机器人播放/暂停状态
    toggleRobotPlay(index) {
      const robot = this.getRobotByIndex(index);
      if (!robot) return;

      // 如果当前机器人正在检查状态，不响应点击
      if (this.checkingIntercomRobots.has(robot.serialNumber)) return;

      // 检查对讲状态
      this.checkIntercomStatus(robot.serialNumber).then(canProceed => {
        if (!canProceed) {
          return; // 如果不能继续，直接返回
        }

        // 原有的切换播放/暂停逻辑
        this.togglePlayPause(robot);
      });
    },
    // 播放下一首歌曲
    playNextSong() {
      if (!this.currentRobot) return;
      this.sendPlayCommand(this.currentRobot.id, 2, 'playNextSongError', () => {
        this.$Notice.success({
          title: this.$translate('success'),
          desc: this.$translate('nextSongSuccess')
        });
      });
    },
    // 播放上一首歌曲
    playPreviousSong() {
      if (!this.currentRobot) return;
      this.sendPlayCommand(this.currentRobot.id, 3, 'playPreviousSongError', () => {
        this.$Notice.success({
          title: this.$translate('success'),
          desc: this.$translate('previousSongSuccess')
        });
      });
    },
    // 停止机器人播放
    stopRobotPlay(index) {
      const robot = this.getRobotByIndex(index);
      if (!robot) return;

      // 保存当前状态用于可能的回滚
      this.saveRobotState(robot.id, {
        playState: robot.playState,
        isPlaying: robot.isPlaying,
        currentSongName: robot.currentSongName
      });

      // 发送停止播放命令 (不受准备中状态影响)
      this.sendApiRequest('notice-server/cmd/audio/playCommand', {
        robotId: robot.id,
        data: {playCommand: 4} // 4: 停止播放
      }, (res) => {
        // 成功回调：更新本地状态
        robot.playState = 1; // 设置为停止状态
        robot.isPlaying = false;

        if (this.currentRobot && this.currentRobot.id === robot.id) {
          this.isPlaying = false;
        }

        // 显示成功提示
        this.$Notice.success({
          title: this.$translate('success'),
          desc: this.$translate('stopSuccess')
        });
      }, 'play_command');
    },
    // 播放指定歌曲
    playSong(song) {
      if (!this.currentRobot) return;

      // 如果当前机器人正在检查状态，不响应点击
      if (this.checkingIntercomRobots.has(this.currentRobot.serialNumber)) return;

      // 如果当前已在播放该歌曲，则暂停/恢复
      if (this.currentRobot.currentSongName === song.fileName && this.currentRobot.isPlaying) {
        this.togglePlay();
        return;
      }
      // 检查对讲状态
      this.checkIntercomStatus(this.currentRobot.serialNumber).then(canProceed => {
        if (!canProceed) {
          return; // 如果不能继续，直接返回
        }
        // 保存当前状态用于可能的回滚
        this.saveRobotState(this.currentRobot.id, {
          playState: this.currentRobot.playState,
          isPlaying: this.currentRobot.isPlaying,
          currentSongName: this.currentRobot.currentSongName
        });

        // 不检查准备中状态，允许在准备中时也能切换歌曲
        this.sendApiRequest('notice-server/cmd/audio/playCommand', {
          robotId: this.currentRobot.id,
          data: {
            playCommand: 1, // 1: 播放指定曲目
            filename: song.fileName // 传入文件名
          }
        }, (res) => {
          // 成功回调：更新本地状态
          const robot = this.currentRobot;
          robot.playState = 3; // 设置为准备中状态
          robot.isPlaying = false;
          // 立即更新当前播放歌曲名称，而不是等待WebSocket消息
          robot.currentSongName = song.fileName;

          // 如果是当前选中的机器人，同步更新视图状态
          if (this.currentRobot && this.currentRobot.id === robot.id) {
            this.currentSongName = song.fileName;
            this.isPlaying = false;
          }

          this.initRobot(); // 调用initRobot刷新所有机器人状态
        }, 'play_command');
      });
    },
    // 切换播放模式
    togglePlayMode(mode) {
      if (!this.currentRobot) return;

      // 如果是准备中状态，提示用户等待
      if (this.isRobotPreparing(this.currentRobot)) return;

      // 保存当前状态用于可能的回滚
      this.saveRobotState(this.currentRobot.id, {
        playMode: this.currentRobot.playMode
      });

      // 映射播放模式参数
      const modeMap = {
        'sequence': 1, // 列表循环
        'random': 2,   // 随机播放
        'single': 3    // 单曲循环
      };

      // 映射播放模式提示文本
      const modeText = {
        'sequence': this.$translate('sequencePlayEnabled'),
        'random': this.$translate('randomPlayEnabled'),
        'single': this.$translate('singleLoopEnabled')
      };

      this.sendApiRequest('notice-server/cmd/audio/playModel', {
        robotId: this.currentRobot.id,
        data: modeMap[mode]
      }, (res) => {
        // 成功回调：更新本地状态
        this.currentRobot.playMode = mode;
        this.playMode = mode;

        // 显示成功提示
        this.$Notice.success({
          title: this.$translate('success'),
          desc: modeText[mode]
        });
      }, 'play_mode');
    },
    toggleMute() {
      // 直接切换静音状态，只调用静音接口
      this.isMuted = !this.isMuted;
      this.updateMuteStatus();
    },
    handleVolumeWheel(event) {
      const delta = event.deltaY > 0 ? -0.1 : 0.1;
      let newVolume = parseFloat(this.volume) + delta;
      newVolume = Math.max(0, Math.min(1, newVolume));
      this.volume = newVolume.toFixed(1);

      // 清除之前的定时器
      if (this.volumeDebounceTimer) {
        clearTimeout(this.volumeDebounceTimer);
      }

      // 设置新的定时器，300ms 后执行
      this.volumeDebounceTimer = setTimeout(() => {
        this.updateVolumeStatus();
      }, 300);
    },
    // 更新音量状态
    updateVolumeStatus() {
      if (!this.currentRobot) return;

      // 如果当前是静音状态，先取消静音
      if (this.isMuted) {
        this.isMuted = false;
        // 调用取消静音API
        this.updateMuteStatus();
        // 等待短暂延时后再设置音量，确保静音状态更新后再调整音量
        setTimeout(() => {
          this.setVolume();
        }, 300);
      } else {
        this.setVolume();
      }
    },
    // 分离出设置音量的方法，便于复用
    setVolume() {
      if (!this.currentRobot) return;

      // 保存当前状态用于可能的回滚
      this.saveRobotState(this.currentRobot.id, {
        volume: this.currentRobot.volume
      });

      const volumeData = Math.round(this.volume * 100);

      this.sendApiRequest('notice-server/cmd/audio/setVolume', {
        robotId: this.currentRobot.id,
        data: {
          volume: volumeData,
          deviceType: 1
        }
      }, (res) => {
        // 成功回调：更新本地状态
        this.currentRobot.volume = volumeData;

        // 显示成功提示
        this.$Notice.success({
          title: this.$translate('success'),
          desc: this.$translate('volumeSetSuccess') + ': ' + volumeData + '%'
        });
      }, 'set_volume');
    },
    // 更新静音状态
    updateMuteStatus() {
      if (!this.currentRobot) return;

      // 保存当前状态用于可能的回滚
      this.saveRobotState(this.currentRobot.id, {
        muted: this.currentRobot.muted
      });

      this.sendApiRequest('notice-server/cmd/audio/toggleMute', {
        robotId: this.currentRobot.id,
        data: {
          muted: this.isMuted,
          deviceType: 1
        }
      }, (res) => {
        // 成功回调：更新本地状态
        this.currentRobot.muted = this.isMuted;

        // 显示成功提示
        this.$Notice.success({
          title: this.$translate('success'),
          desc: this.isMuted ? this.$translate('muteSuccess') : this.$translate('unmuteSuccess')
        });
      }, 'toggle_mute');
    },
    updateBtnStatus(url,robotId,data,callback){
      api.postData(url,{robotId,data},this).then(res => {
        if (res.state === 0) {
            this.$Notice.error({
              title: this.$translate('tip'),
              desc: res.msg
            });
          } else {
            callback()
          }
      });
    },
    calculateWaveCount() {
      const container = this.$refs.waveContainer;
      if (container) {
        const containerWidth = container.offsetWidth - 40; // 减去padding
        // 每个波浪条宽度(3px) + 间距(2px) = 5px
        this.waveCount = Math.floor(containerWidth / 5);
      }
    },
    initParticleAnimation() {
      this.canvas = this.$refs.particleCanvas;
      this.ctx = this.canvas.getContext('2d');

      const resizeCanvas = () => {
        this.canvas.width = this.canvas.offsetWidth;
        this.canvas.height = this.canvas.offsetHeight;
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      // 初始化星星
      this.stars = Array.from({ length: this.starCount }, () => ({
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.5,
        blinkSpeed: Math.random() * 0.02
      }));

      // 初始化粒子
      this.particles = Array.from({ length: this.particleCount }, () => ({
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        size: Math.random() * 4 + 2,
        speedX: (Math.random() - 0.5) * 1.5,
        speedY: (Math.random() - 0.5) * 1.5,
        hue: Math.random() * 60 + 280, // 紫色到红色范围
        opacity: Math.random() * 0.6 + 0.4,
        blur: Math.random() * 2 + 1
      }));

      // 初始化音符
      this.notes = Array.from({ length: this.noteCount }, () => ({
        x: Math.random() * this.canvas.width,
        y: this.canvas.height + Math.random() * 100,
        size: Math.random() * 30 + 20,
        speedY: -Math.random() * 1 - 0.5,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.02,
        hue: Math.random() * 60 + 280,
        opacity: Math.random() * 0.5 + 0.5
      }));

      this.animate();
    },

    drawNote(ctx, x, y, size, rotation, hue, opacity) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.fillStyle = `hsla(${hue}, 100%, 70%, ${opacity})`;
      ctx.shadowBlur = 15;
      ctx.shadowColor = `hsla(${hue}, 100%, 70%, ${opacity})`;

      // 绘制音符头部
      ctx.beginPath();
      ctx.ellipse(0, 0, size * 0.6, size * 0.4, 0, 0, Math.PI * 2);
      ctx.fill();

      // 绘制音符竖线
      ctx.fillRect(size * 0.5, -size, size * 0.15, size);
      ctx.restore();
    },

    animate(currentTime) {
      // 节流控制，限制帧率
      if (currentTime && currentTime - this.lastTime < 16) { // 约60fps
        this.animationFrame = requestAnimationFrame((time) => this.animate(time));
        return;
      }
      this.lastTime = currentTime;

      this.time += 0.01;
      this.hueRotate += 0.5;

      // 创建渐变背景
      const gradient = this.ctx.createLinearGradient(0, 0, 0, this.canvas.height);
      gradient.addColorStop(0, 'rgba(20, 0, 40, 0.95)');
      gradient.addColorStop(1, 'rgba(40, 0, 80, 0.95)');
      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

      // 绘制星星
      this.stars.forEach(star => {
        star.opacity = 0.5 + Math.sin(this.time * star.blinkSpeed) * 0.5;
        this.ctx.beginPath();
        this.ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        this.ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        this.ctx.fill();
      });

      // 优化粒子渲染
      this.particles.forEach((particle, index) => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // 边界检查
        if (particle.x < 0) particle.x = this.canvas.width;
        if (particle.x > this.canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = this.canvas.height;
        if (particle.y > this.canvas.height) particle.y = 0;

        // 绘制发光粒子
        this.ctx.beginPath();
        this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        const gradient = this.ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, particle.size * 2
        );
        gradient.addColorStop(0, `hsla(${particle.hue + this.hueRotate}, 100%, 70%, ${particle.opacity})`);
        gradient.addColorStop(1, 'hsla(0, 0%, 0%, 0)');

        this.ctx.fillStyle = gradient;
        this.ctx.shadowBlur = particle.blur * 5;
        this.ctx.shadowColor = `hsla(${particle.hue + this.hueRotate}, 100%, 70%, ${particle.opacity})`;
        this.ctx.fill();

        // 优化连接线绘制：只连接距离较近的粒子
        if (index % 2 === 0) { // 每隔一个粒子才检查连接线
          for (let i = index + 1; i < Math.min(index + 8, this.particles.length); i++) {
            const other = this.particles[i];
            const dx = particle.x - other.x;
            const dy = particle.y - other.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 100) {
              this.ctx.beginPath();
              this.ctx.strokeStyle = `hsla(${particle.hue + this.hueRotate}, 100%, 70%, ${0.15 * (1 - distance/100)})`;
              this.ctx.lineWidth = 1;
              this.ctx.moveTo(particle.x, particle.y);
              this.ctx.lineTo(other.x, other.y);
              this.ctx.stroke();
            }
          }
        }
      });

      // 绘制音符
      this.notes.forEach(note => {
        note.y += note.speedY;
        note.rotation += note.rotationSpeed;

        if (note.y < -note.size * 2) {
          note.y = this.canvas.height + note.size;
          note.x = Math.random() * this.canvas.width;
        }

        this.drawNote(
          this.ctx,
          note.x,
          note.y,
          note.size,
          note.rotation,
          note.hue + this.hueRotate,
          note.opacity
        );
      });

      this.animationFrame = requestAnimationFrame((time) => this.animate(time));
    },
    // 添加页码切换方法
    changePage(newPage) {
      if (newPage < 1 || newPage > this.current || newPage === this.page) return;
      this.page = newPage;
      this.getList(); // 调用已有的获取列表方法
    },
    // 切换歌曲列表显示状态
    togglePlaylist() {
      const robotId = this.currentRobot?.id
      if(!robotId){
        this.$Notice.error({
          title: this.$translate('tip'),
          desc: this.$translate('pleaseSelectRobot')
        });
        return
      }
      this.showPlaylist = !this.showPlaylist;
      if (this.showPlaylist) {
        this.getSongList(robotId);
      }
    },

    // 获取歌曲列表
    getSongList(robotId) {
      api.getData('notice-server/sound/all?robotId='+robotId).then(res => {
        if (res.state === 0) {
          this.$Notice.error({
            title: this.$translate('tip'),
            desc: res.msg
          });
        } else {
          this.songList = (res.data || []).map(song => ({
            ...song,
            id: song.soundId || ''
          }));
        }
      }).catch(error => {
        this.$Notice.error({
          title: this.$translate('error'),
          desc: this.$translate('getSongListError')
        });
      });
    },
    syncRobot(index) {
      const robot = this.getRobotByIndex(index);
      if (!robot) return;

      this.sendApiRequest('notice-server/sound/manualSync', {
        robotId: robot.id
      }, (res) => {
        // 成功回调：更新本地状态
        this.$Notice.success({
          title: this.$translate('success'),
          desc: this.$translate('syncSuccess')
        });
        // 同步成功后重新获取歌曲列表
        if (this.showPlaylist && this.currentRobot && this.currentRobot.id === robot.id) {
          this.getSongList(robot.id);
        }
      }, 'play_list');
    },
    // 添加几个通用方法来替代重复代码

    // 保存操作前状态
    saveRobotState(robotId, stateData) {
      if (!robotId || !stateData) return;

      // 如果是第一次保存该机器人状态，创建对象
      if (!this.previousStates[robotId]) {
        this.previousStates[robotId] = {};
      }

      // 合并状态数据
      this.previousStates[robotId] = {
        ...this.previousStates[robotId],
        ...stateData
      };
    },

    // 通用的API请求方法
    sendApiRequest(endpoint, params, successCallback, errorType) {
      return api.postData(endpoint, params)
        .then(res => {
          if (res.state === 0) {
            this.$Notice.error({
              title: this.$translate('tip'),
              desc: res.msg
            });
            return false;
          }
          // 成功响应由WebSocket通知更新状态
          if (typeof successCallback === 'function') {
            successCallback(res);
          }
          return true;
        })
        .catch(error => {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: this.$translate(errorType + 'Error')
          });

          // 如果提供了错误类型和机器人ID，尝试回滚状态
          if (errorType && params.robotId) {
            this.rollbackState('cmd_resp_' + errorType + '_error', params.robotId);
          }
          return false;
        });
    },

    // 检查机器人是否在准备中状态
    isRobotPreparing(robot) {
      if (!robot) return false;

      if (robot.playState === 3) { // 准备中状态
        this.$Notice.warning({
          title: this.$translate('tip'),
          desc: this.$translate('preparingPleaseWait')
        });
        return true;
      }
      return false;
    },

    // 通用的播放暂停控制
    togglePlayPause(robot, isMainControl = false) {
      if (!robot) return;

      // 检查是否处于准备中状态
      if (this.isRobotPreparing(robot)) return;

      // 保存当前状态用于可能的回滚
      this.saveRobotState(robot.id, {
        playState: robot.playState,
        isPlaying: robot.isPlaying
      });

      // 判断当前播放状态
      // 如果是播放中(4)或暂停(2)状态，则使用pauseOrResume接口
      if (robot.playState === 4 || robot.playState === 2) {
        this.sendApiRequest('notice-server/cmd/audio/pauseOrResume', {
          robotId: robot.id
        }, (res) => {
          // 成功回调：更新本地状态
          const newPlayState = robot.playState === 4 ? 2 : 4;
          robot.playState = newPlayState;
          robot.isPlaying = newPlayState === 4;

          if (isMainControl || this.currentRobot && this.currentRobot.id === robot.id) {
            this.isPlaying = robot.isPlaying;
          }

          // 显示成功提示
          this.$Notice.success({
            title: this.$translate('success'),
            desc: robot.isPlaying ? this.$translate('playSuccess') : this.$translate('pauseSuccess')
          });
        }, 'pause_or_resume');
      }
      // 如果是已停止(1)状态，则使用playCommand接口开始播放
      else if (robot.playState === 1) {
        // 先获取歌曲列表，播放第一首歌
        this.fetchSongListAndPlay(robot.id, isMainControl);
      }
    },

    // 获取歌曲列表并播放第一首歌
    fetchSongListAndPlay(robotId, isMainControl = false) {
      api.getData('notice-server/sound/all?robotId=' + robotId)
        .then(res => {
          if (res.state === 0) {
            this.$Notice.error({
              title: this.$translate('tip'),
              desc: res.msg
            });
            return;
          }

          const songList = (res.data || []);

          if (songList.length === 0) {
            // 没有歌曲，提示用户
            this.$Notice.warning({
              title: this.$translate('tip'),
              desc: this.$translate('noSongsAvailable')
            });
            return;
          }

          // 有歌曲，播放第一首
          const firstSong = songList[0];

          this.sendApiRequest('notice-server/cmd/audio/playCommand', {
            robotId: robotId,
            data: {
              playCommand: 1, // 1: 播放指定曲目
              filename: firstSong.fileName // 传入第一首歌的文件名
            }
          }, (res) => {
            // 成功回调：更新本地状态
            const { robot } = this.getRobot(robotId);
            if (robot) {
              robot.playState = 3; // 设置为准备中状态 (原来是2，现在是3)
              robot.isPlaying = false;
              robot.currentSongName = firstSong.fileName;

              if (isMainControl || this.currentRobot && this.currentRobot.id === robotId) {
                this.isPlaying = false;
                this.currentSongName = firstSong.fileName;
              }
            }
          }, 'play_command');
        })
        .catch(error => {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: this.$translate('getSongListError')
          });
        });
    },

    // 通用的播放命令发送
    sendPlayCommand(robotId, command, errorMsg = 'playCommandError', successCallback = null) {
      const { robot } = this.getRobot(robotId);
      if (!robot) return;

      // 检查是否处于准备中状态
      if (this.isRobotPreparing(robot)) return;

      // 保存当前状态用于可能的回滚
      this.saveRobotState(robotId, {
        playState: robot.playState,
        isPlaying: robot.isPlaying,
        currentSongName: robot.currentSongName
      });

      // 发送播放命令
      return this.sendApiRequest('notice-server/cmd/audio/playCommand', {
        robotId: robotId,
        data: {playCommand: command}
      }, (res) => {
        // 成功回调：更新本地状态
        if (command === 4) { // 停止播放命令
          robot.playState = 1; // 设置为停止状态 (原来是0，现在是1)
          robot.isPlaying = false;

          if (this.currentRobot && this.currentRobot.id === robotId) {
            this.isPlaying = false;
          }
        } else { // 下一首(2)/上一首(3)
          robot.playState = 3; // 设置为准备中状态 (原来是2，现在是3)

          if (this.currentRobot && this.currentRobot.id === robotId) {
            this.isPlaying = false;
          }
        }

        // 如果有自定义成功回调，执行它
        if (successCallback && typeof successCallback === 'function') {
          successCallback(res);
        }
      }, 'play_command');
    },

    // 添加获取机器人索引或实例的通用方法
    getRobot(robotId) {
      if (!robotId) return { robot: null, index: -1, isCurrentRobot: false };

      const index = this.robotList.findIndex(robot => robot.id === robotId);
      if (index === -1) return { robot: null, index: -1, isCurrentRobot: false };

      const robot = this.robotList[index];
      const isCurrentRobot = this.currentRobot && this.currentRobot.id === robotId;

      return { robot, index, isCurrentRobot };
    },

    // 通过索引获取机器人
    getRobotByIndex(index) {
      if (index < 0 || index >= this.robotList.length) return null;
      return this.robotList[index];
    },

    // 检查对讲状态
    checkIntercomStatus(serialNumber) {
      return new Promise(async (resolve) => {
        // 将当前机器人ID添加到检查集合中
        this.checkingIntercomRobots.add(serialNumber);
        const canProceed = await this.checkIntercomStatusAgain(serialNumber)
        if (!canProceed) {
          // 正在对讲中，弹出确认对话框
          this.$Modal.confirm({
              title: this.$translate('tip'),
              content: this.$translate('robotIntercomActiveStopToPlay'),
              okText: this.$translate('stopIntercom'),
              cancelText: this.$translate('cancel'),
              onOk: () => {
                // 停止对讲前，将机器人ID再次添加到检查集合
                this.checkingIntercomRobots.add(serialNumber);

                // 停止对讲
                this.stopIntercom(serialNumber).then(success => {
                  this.checkingIntercomRobots.delete(serialNumber);
                  if (success) {
                    // 再次检查状态确认对讲已停止
                    setTimeout(async () => {
                      const canProceed = await this.checkIntercomStatusAgain(serialNumber)
                      if (canProceed) {
                        resolve(true);
                      } else {
                        resolve(false);
                      }
                    }, 1000);
                  } else {
                    this.$Notice.error({
                      title: this.$translate('error'),
                      desc: this.$translate('stopIntercomFailed')
                    });
                    resolve(false);
                  }
                });
              },
              onCancel: () => {
                // 用户取消，不继续
                resolve(false);
              }
            });
          return;
        }else{
          resolve(true);
        }
      });
    },

    // 检查对讲状态
    checkIntercomStatusAgain(serialNumber) {
      return new Promise((resolve) => {
        api.getData(
          `${this.VoiceIntercomURL}/api/play/broadcast/status/${serialNumber}/${serialNumber}?linkId=${this.$store.state.user.token}`,
          this,
          true
        ).then(res => {
          this.checkingIntercomRobots.delete(serialNumber);
          if (res.code === 0 && res.data && res.data.inUse === false) {
            // 对讲已停止，可以播放音乐
            resolve(true);
          } else {
            // 对讲未停止
            this.$Notice.error({
              title: this.$translate('error'),
              desc: res.msg
            });
            resolve(false);
          }
        }).catch(error => {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: this.$translate('checkIntercomStatusFailed')
          });
          this.checkingIntercomRobots.delete(serialNumber);
          resolve(false);
        });
      });
    },

    // 停止对讲
    stopIntercom(serialNumber) {
      return new Promise((resolve) => {
        api.getData(
          `${this.VoiceIntercomURL}/api/play/broadcast/stop/${serialNumber}/${serialNumber}?linkId=${this.$store.state.user.token}`,
          this,
          true
        ).then(res => {
          if (res.code !== 0) {
            this.$Notice.error({
              title: this.$translate('error'),
              desc: res.msg || this.$translate('stopIntercomFailed')
            });
            resolve(false);
          } else {
            resolve(true);
          }
        }).catch(error => {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: this.$translate('stopIntercomFailed')
          });
          resolve(false);
        });
      });
    }
  }
}
</script>

<style lang="less" scoped>
.music-playback-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.gradient-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    #0a192f 0%,
    #121e34 25%,
    #1e3a5f 50%,
    #121e34 75%,
    #0a192f 100%
  );
  animation: gradientShift 15s ease infinite;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(64, 169, 255, 0.1) 0%,
      transparent 60%
    );
    pointer-events: none;
  }

  &:after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: repeating-linear-gradient(
      transparent 0%,
      rgba(0, 176, 255, 0.05) 5%,
      transparent 10%
    );
    animation: lightEffect 10s linear infinite;
    transform: rotate(45deg);
  }
}

.floating-panel {
  position: absolute;
  left: 2%;
  top: 50%;
  transform: translateY(-50%);
  width: 38%;
  height: 95%;
  border: 1px solid rgba(64, 169, 255, 0.2);
  border-radius: 12px;
  background: rgba(16, 32, 58, 0.6);
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2), 0 0 15px rgba(64, 169, 255, 0.2);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  padding: 20px 24px;

  .left-title, .right-title {
    font-size: 32px;
    font-weight: bold;
    color: #ffffff;
  }
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes lightEffect {
  0% {
    transform: rotate(45deg) translateY(0);
  }
  100% {
    transform: rotate(45deg) translateY(-50%);
  }
}

.robot-list {
  padding: 0 20px;
  height: ~'calc(100% - 180px)'; // 使用 Less 语法包裹 calc
  overflow-y: auto;
  overflow-x: hidden;

  // 自定义滚动条样式
  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  // 添加平滑滚动
  scroll-behavior: smooth;

  // 优化移动端滚动体验
  -webkit-overflow-scrolling: touch;
}

.robot-item {
  display: flex;
  align-items: center;
  padding: 15px;
  margin-bottom: 10px;
  background: rgba(25, 45, 70, 0.7);
  border-radius: 12px;
  border-left: 3px solid rgba(64, 169, 255, 0.5);
  backdrop-filter: blur(4px);
  transition: all 0.3s ease;
  transform: translateZ(0);
  will-change: transform;

  &:hover {
    background: rgba(35, 55, 85, 0.8);
    border-left: 3px solid rgba(64, 169, 255, 0.9);
    box-shadow: 0 0 15px rgba(64, 169, 255, 0.3);
  }

  &:last-child {
    margin-bottom: 20px;
  }
}

.robot-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 15px;
  animation: float-updown 2.2s ease-in-out infinite;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

@keyframes float-updown {
  0% { transform: translateY(0); }
  50% { transform: translateY(-7px); }
  100% { transform: translateY(0); }
}

.robot-info {
  flex: 1;

  .robot-name {
    font-size: 18px;
    font-weight: 500;
    color: #ffffff;
    margin-bottom: 4px;
  }

  .robot-serial {
    font-size: 14px;
    color:  #ffca00;
  }
}

.robot-controls {
  display: flex;
  align-items: center;
  gap: 12px;

  .sync-btn, .stop-btn {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(16, 32, 58, 0.9);
    border: 1px solid rgba(64, 169, 255, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 0 15px rgba(64, 169, 255, 0.5);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .sync-icon, .stop-icon {
    font-size: 24px;
    color: rgba(64, 169, 255, 0.9);
    transition: all 0.3s ease;
  }

  .stop-btn {
    border-color: rgba(255, 99, 71, 0.5);

    &:hover {
      box-shadow: 0 0 15px rgba(255, 99, 71, 0.5);
    }

    .stop-icon {
      color: rgba(255, 99, 71, 0.9);
    }
  }

  .play-status {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(16, 32, 58, 0.9);
    border: 1px solid rgba(64, 169, 255, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 0 15px rgba(64, 169, 255, 0.5);
    }

    .play-triangle {
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 12px 0 12px 20px;
      border-color: transparent transparent transparent rgba(64, 169, 255, 0.9);
      transition: all 0.3s ease;
      margin-left: 3px;
    }

    &.playing {
      background: rgba(16, 32, 58, 0.9);

      .wave-animation {
        display: flex;
        align-items: center;
        height: 24px;
        gap: 2px;

        span {
          display: inline-block;
          width: 3px;
          height: 100%;
          background: rgba(64, 169, 255, 0.9);
          border-radius: 3px;
          animation: wave 1.2s ease-in-out infinite;

          &:nth-child(1) { animation-delay: 0s; }
          &:nth-child(2) { animation-delay: 0.3s; }
          &:nth-child(3) { animation-delay: 0.6s; }
          &:nth-child(4) { animation-delay: 0.9s; }
        }
      }
    }

    &.preparing {
      background: rgba(16, 32, 58, 0.9);
      cursor: not-allowed;

      .loading-animation {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 24px;
        gap: 4px;

        span {
          display: inline-block;
          width: 6px;
          height: 6px;
          background: rgba(255, 193, 7, 0.9);
          border-radius: 50%;
          animation: pulse 1.5s ease-in-out infinite;

          &:nth-child(1) { animation-delay: 0s; }
          &:nth-child(2) { animation-delay: 0.5s; }
          &:nth-child(3) { animation-delay: 1s; }
        }
      }
    }

    &.checking {
      background: rgba(255, 152, 0, 0.6);
      border-color: rgba(255, 152, 0, 0.6);
      cursor: wait;
    }
  }
}

@keyframes wave {
  0%, 100% { height: 8px; }
  50% { height: 24px; }
}

.robot-item {
  cursor: pointer;
  &.active {
    background: rgba(13, 71, 161, 0.6);
    border-left: 3px solid rgba(33, 150, 243, 1);
    box-shadow: 0 0 20px rgba(33, 150, 243, 0.3);

    &:hover {
      background: rgba(25, 118, 210, 0.7);
    }
  }
}

// 右侧面板样式
.right-panel {
  left: auto;
  right: 2%;
  width: 56%;
  border: none;
  background: none;
  box-shadow: none;
}

.player-container {
  padding: 24px;
  height: 100%;
}

.player-image {
  position: relative;
  width: 100%;
  height: 50%;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 24px;
  background: linear-gradient(to bottom,
    rgba(10, 25, 47, 0.98) 0%,
    rgba(30, 58, 95, 0.98) 100%
  );
  border: 1px solid rgba(64, 169, 255, 0.3);
  box-shadow: 0 0 30px rgba(64, 169, 255, 0.2);

  .particle-canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    filter: blur(0.5px);
  }

  .player-info {
    position: absolute;
    bottom: 24px;
    left: 24px;
    color: #fff;
    z-index: 1;
    width: 80%;

    .current-name {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 8px;
      text-shadow: 0 0 20px rgba(64, 169, 255, 0.8);
    }

    .current-serial {
      font-size: 16px;
      opacity: 0.8;
      text-shadow: 0 0 15px rgba(64, 169, 255, 0.6);
    }

    .current-song {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.9);
      margin-top: 8px;
      text-shadow: 0 0 15px rgba(64, 169, 255, 0.6);
      max-width: 80%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.wave-container {
  height: 15%;
  width: 90%;
  margin: 10% auto 10% 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0 20px;
  background: rgba(16, 32, 58, 0.7);
  border-radius: 10px;
  border: 1px solid rgba(64, 169, 255, 0.2);
  position: relative;
  overflow: hidden;

  /* 添加背景渐变效果 */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to right,
      rgba(64, 169, 255, 0.05),
      rgba(138, 43, 226, 0.05),
      rgba(255, 105, 180, 0.05),
      rgba(64, 169, 255, 0.05)
    );
    background-size: 400% 100%;
    animation: gradientMove 8s linear infinite;
    z-index: 0;
  }

  .wave-bar {
    width: 3px;
    height: 2px;
    background-color: #fff;
    border-radius: 0;
    transition: all 0.3s ease;
    position: relative;
    z-index: 1;

    &.short {
      height: 2px;
      opacity: 0.6;
    }
  }

  &.playing {
    gap: 2px;

    .wave-bar {
      border-radius: 2px;
      animation: waveAnimation 1s ease-in-out infinite;

      /* 使用多种颜色的波浪条 */
      &:nth-child(5n+1) {
        background: linear-gradient(to bottom, #ff3d00, #ff9e80);
        box-shadow: 0 0 8px rgba(255, 61, 0, 0.6);
      }

      &:nth-child(5n+2) {
        background: linear-gradient(to bottom, #2979ff, #82b1ff);
        box-shadow: 0 0 8px rgba(41, 121, 255, 0.6);
      }

      &:nth-child(5n+3) {
        background: linear-gradient(to bottom, #00e676, #b9f6ca);
        box-shadow: 0 0 8px rgba(0, 230, 118, 0.6);
      }

      &:nth-child(5n+4) {
        background: linear-gradient(to bottom, #d500f9, #ea80fc);
        box-shadow: 0 0 8px rgba(213, 0, 249, 0.6);
      }

      &:nth-child(5n+5) {
        background: linear-gradient(to bottom, #ffea00, #ffff8d);
        box-shadow: 0 0 8px rgba(255, 234, 0, 0.6);
      }

      &.short {
        animation: waveAnimationShort 1s ease-in-out infinite;
        opacity: 0.8;
      }
    }
  }
}

@keyframes gradientMove {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes waveAnimation {
  0% {
    height: 10px;
    opacity: 0.7;
  }
  50% {
    height: 80px;
    opacity: 1;
  }
  100% {
    height: 10px;
    opacity: 0.7;
  }
}

@keyframes waveAnimationShort {
  0% {
    height: 5px;
    opacity: 0.5;
  }
  50% {
    height: 50px;
    opacity: 0.8;
  }
  100% {
    height: 5px;
    opacity: 0.5;
  }
}

.generate-delays(@n, @i: 1) when (@i =< @n) {
  &:nth-child(@{i}) {
    animation-delay: (@i * 0.03s);
  }
  .generate-delays(@n, (@i + 1));
}

.wave-container.playing .wave-bar {
  .generate-delays(200);
}

.player-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  padding: 0 40px;

  .mode-controls {
    display: flex;
    gap: 20px;

    .mode-btn {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.15);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;

      .svg-icon {
        font-size: 20px;
        color: rgba(255, 255, 255, 0.8);
        transition: all 0.3s ease;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.25);
        transform: scale(1.05);
        .svg-icon {
          color: #fff;
        }
      }

      &.active {
        background: rgba(92, 112, 213, 0.6);
        .svg-icon {
          color: #fff;
        }

        &:hover {
          background: rgba(92, 112, 213, 0.8);
        }
      }
    }
  }

  .control-buttons {
    display: flex;
    align-items: center;
    gap: 20px;

    .main-play-btn {
      min-width: 60px;
      min-height: 60px;
      width: 5vw;
      height: 5vw;
      aspect-ratio: 1;
      border-radius: 50%;
      background: linear-gradient(145deg, #1565c0, #0d47a1);
      border: 2px solid rgba(64, 169, 255, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;
      box-shadow: 0 4px 15px rgba(33, 150, 243, 0.3), 0 0 20px rgba(64, 169, 255, 0.4);

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 6px 20px rgba(33, 150, 243, 0.4), 0 0 30px rgba(64, 169, 255, 0.6);
      }

      &.preparing {
        background: linear-gradient(145deg, #ff9800, #f57c00);
        border-color: rgba(255, 193, 7, 0.6);
        box-shadow: 0 4px 15px rgba(255, 152, 0, 0.3), 0 0 20px rgba(255, 193, 7, 0.4);
        cursor: wait;

        &:hover {
          background: linear-gradient(145deg, #ffa726, #fb8c00);
          box-shadow: 0 6px 20px rgba(255, 152, 0, 0.4), 0 0 30px rgba(255, 193, 7, 0.6);
        }

        .loading-animation {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 40%;
          gap: 6px;

          span {
            display: inline-block;
            width: 8px;
            height: 8px;
            background: #fff;
            border-radius: 50%;
            animation: pulse 1.5s ease-in-out infinite;

            &:nth-child(1) { animation-delay: 0s; }
            &:nth-child(2) { animation-delay: 0.5s; }
            &:nth-child(3) { animation-delay: 1s; }
          }
        }
      }

      .play-icon {
        width: 40%;
        height: 40%;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;

        // 播放状态
        &.play-btn-status {
          margin-left: 4px; // 播放图标稍微向右偏移以视觉居中
        }

        // 暂停状态
        &.pause-btn-status {
          margin-left: 0;
        }
      }
    }

    .next-prev-btn {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: linear-gradient(145deg, #1976d2, #1565c0);
      border: 2px solid rgba(64, 169, 255, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 2px 10px rgba(33, 150, 243, 0.25), 0 0 15px rgba(64, 169, 255, 0.2);

      &:hover {
        background: linear-gradient(145deg, #1e88e5, #1976d2);
        transform: scale(1.05);
        box-shadow: 0 4px 15px rgba(33, 150, 243, 0.35), 0 0 20px rgba(64, 169, 255, 0.3);
      }

      &:active {
        transform: scale(0.95);
      }

      .control-icon {
        font-size: 20px;
        color: #fff;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
      }
    }
  }

  .volume-control {
    display: flex;
    align-items: center;
    gap: 12px;

    .volume-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }

    .volume-slider {
      width: 120px;

      input[type="range"] {
        width: 100%;
        height: 4px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 2px;
        outline: none;

        &::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 12px;
          height: 12px;
          background: #ffffff;
          border-radius: 50%;
          cursor: pointer;
        }
      }
    }

    .volume-display {
      min-width: 45px;
      text-align: center;

      .volume-value {
        font-size: 14px;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.9);
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
        background: rgba(255, 255, 255, 0.1);
        padding: 4px 8px;
        border-radius: 12px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(4px);
        transition: all 0.3s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
        }
      }
    }

    // 添加歌曲列表按钮
    .playlist-btn {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      margin-left: 12px;

      &:hover {
        background: rgba(255, 255, 255, 0.2);
        transform: scale(1.05);
      }
    }
  }
}

@keyframes waveAnimation {
  0%, 100% {
    height: 40px;
  }
  50% {
    height: 80px;
  }
}

@keyframes waveAnimationShort {
  0%, 100% {
    height: 25px;
  }
  50% {
    height: 50px;
  }
}

.mode-icon {
  font-size: 24px;
  color: #fff;
  transition: all 0.3s;
}

.mode-btn:active {
  transform: scale(0.95) !important;
}

// 添加分页样式
.pagination {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 10px;
  background: rgba(16, 32, 58, 0.7);
  border-radius: 20px;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(64, 169, 255, 0.3);

  .page-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(33, 150, 243, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover:not(.disabled) {
      background: rgba(33, 150, 243, 0.5);
      transform: scale(1.1);
      box-shadow: 0 0 15px rgba(64, 169, 255, 0.5);
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .page-icon {
      font-size: 20px;
      color: #fff;
    }
    .arrow-right{
      transform: rotate(180deg)
    }
  }

  .page-info {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #fff;
    font-size: 18px;
    font-weight: 500;

    .current-page {
      min-width: 30px;
      text-align: center;
      animation: numberChange 0.3s ease;
    }

    .page-divider {
      width: 20px;
      height: 2px;
      background: linear-gradient(90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.8) 50%,
        transparent 100%
      );
    }

    .total-page {
      min-width: 30px;
      text-align: center;
      opacity: 0.8;
    }
  }
}

@keyframes numberChange {
  0% {
    transform: translateY(-10px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.robot-song {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 4px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.playlist-drawer {
  position: absolute;
  top: 0;
  right: -350px;
  width: 350px;
  height: 100%;
  background: linear-gradient(135deg,
    rgba(10, 25, 47, 0.95) 0%,
    rgba(21, 67, 126, 0.95) 100%
  );
  backdrop-filter: blur(20px);
  box-shadow:
    -8px 0 30px rgba(0, 0, 0, 0.3),
    inset 1px 1px 0px rgba(64, 169, 255, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  border-radius: 20px 0 0 20px;
  border-left: 1px solid rgba(64, 169, 255, 0.3);

  &.show {
    right: 0;
  }

  .drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px 0 0 0;

    .drawer-title {
      font-size: 20px;
      font-weight: bold;
      color: #fff;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      letter-spacing: 1px;
    }

    .close-btn {
      cursor: pointer;
      transition: all 0.3s ease;
      width: 25px;
      height: 25px;
      display: flex;
      align-items: center;
      justify-content: center;

      .close-icon {
        font-size: 25px;
        color: rgba(255, 255, 255, 0.8);
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
      }

      &:hover {
        transform: scale(1.2) rotate(90deg);
        .close-icon {
          color: #fff;
        }
      }
    }
  }

  .song-list {
    height: ~'calc(100% - 77px)';
    overflow-y: auto;
    padding: 15px;

    &::-webkit-scrollbar {
      width: 6px;
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 3px;

      &:hover {
        background: rgba(255, 255, 255, 0.3);
      }
    }

    .song-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 15px;
      margin-bottom: 12px;
      border-radius: 12px;
      background: linear-gradient(
        145deg,
        rgba(16, 32, 58, 0.7) 0%,
        rgba(13, 71, 161, 0.4) 100%
      );
      transition: all 0.3s ease;
      border: 1px solid rgba(64, 169, 255, 0.2);
      box-shadow:
        0 4px 15px rgba(0, 0, 0, 0.1),
        inset 0 0 0 1px rgba(64, 169, 255, 0.05);
      backdrop-filter: blur(5px);

      &:hover {
        transform: translateX(-5px);
        background: linear-gradient(
          145deg,
          rgba(13, 71, 161, 0.5) 0%,
          rgba(21, 101, 192, 0.3) 100%
        );
        border-color: rgba(64, 169, 255, 0.4);
        box-shadow:
          0 6px 20px rgba(0, 0, 0, 0.15),
          0 0 15px rgba(64, 169, 255, 0.3);
      }

      &.playing {
        background: linear-gradient(
          145deg,
          rgba(13, 71, 161, 0.6) 0%,
          rgba(33, 150, 243, 0.4) 100%
        );
        border-color: rgba(64, 169, 255, 0.6);
        box-shadow:
          0 6px 20px rgba(33, 150, 243, 0.2),
          0 0 15px rgba(64, 169, 255, 0.4);

        .song-name {
          color: #fff;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      }

      .song-info {
        flex: 1;
        .song-name {
          font-size: 15px;
          color: rgba(255, 255, 255, 0.9);
          max-width: 250px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          transition: all 0.3s ease;
        }
      }

      .song-controls {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .play-btn {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: rgba(255,255,255,0.15);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          transform: scale(1.1);
          background: rgba(255,255,255,0.25);
        }

        &.playing {
          background: #4583c3;

          &:hover {
            background: #72b7ff;
          }
        }

        .play-icon {
          font-size: 18px;
          color: #fff;
          transition: all 0.3s ease;
        }
      }
    }
  }
}

@keyframes playingWave {
  0%, 100% {
    height: 5px;
    opacity: 0.5;
  }
  50% {
    height: 15px;
    opacity: 1;
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(0.8);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
}

.song-item {
  /* 现有样式 */

  &.preparing {
    background: linear-gradient(
      145deg,
      rgba(255, 152, 0, 0.5) 0%,
      rgba(255, 193, 7, 0.3) 100%
    );
    border-color: rgba(255, 193, 7, 0.5);
    box-shadow:
      0 6px 20px rgba(255, 152, 0, 0.2),
      0 0 15px rgba(255, 193, 7, 0.3);

    .song-name {
      color: #fff;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
  }

  /* 其他样式 */
}

.play-btn {
  /* 现有样式 */

  &.preparing {
    background: linear-gradient(145deg, #ff9800, #f57c00);
    border-color: rgba(255, 193, 7, 0.5);
    box-shadow: 0 0 10px rgba(255, 152, 0, 0.4);

    &:hover {
      background: linear-gradient(145deg, #ffa726, #fb8c00);
    }

    .loading-animation {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 70%;
      gap: 3px;

      span {
        display: inline-block;
        width: 4px;
        height: 4px;
        background: #fff;
        border-radius: 50%;
        animation: pulse 1.5s ease-in-out infinite;

        &:nth-child(1) { animation-delay: 0s; }
        &:nth-child(2) { animation-delay: 0.5s; }
        &:nth-child(3) { animation-delay: 1s; }
      }
    }
  }

  /* 其他样式 */
}

.loading-animation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.loading-animation span {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #fff;
  border-radius: 50%;
  animation: dot-pulse 1.5s infinite ease-in-out;
}

.loading-animation span:nth-child(1) {
  animation-delay: 0s;
}

.loading-animation span:nth-child(2) {
  animation-delay: 0.3s;
}

.loading-animation span:nth-child(3) {
  animation-delay: 0.6s;
}

@keyframes dot-pulse {
  0%, 100% {
    transform: scale(0.8);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
}
</style>
